/**
 * Sign-in form. Per FR-002, FR-012. Handles MFA inline.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormError } from "@/components/FormError";
import { doConfirmMfa, doSignIn, useAuthRole } from "@/lib/auth/useAuth";

const credentialsSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});
type Credentials = z.infer<typeof credentialsSchema>;

const ROLE_LANDING: Record<string, string> = {
  customer: "/dashboard",
  tenant_admin: "/admin/queue",
  platform_admin: "/admin/platform/tenants",
};

export function SignIn() {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const role = useAuthRole();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Credentials>({ resolver: zodResolver(credentialsSchema) });

  const goToLanding = () => {
    navigate(role ? (ROLE_LANDING[role] ?? "/dashboard") : "/dashboard");
  };

  const onSignIn = async (values: Credentials) => {
    setSubmitting(true);
    setServerError(null);
    const result = await doSignIn(values);
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "Sign-in failed");
      return;
    }
    if (result.mfaRequired) {
      setMfaRequired(true);
      return;
    }
    goToLanding();
  };

  const onMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setServerError(null);
    const result = await doConfirmMfa(mfaCode);
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "MFA failed");
      return;
    }
    goToLanding();
  };

  return (
    <div className="mx-auto max-w-md px-5 py-10 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        {mfaRequired ? "Two-factor" : "Sign in"}
      </p>
      <h1 className="mb-3 font-serif text-3xl text-starz-navy md:text-4xl">
        {mfaRequired ? (
          <>
            Verify <em className="text-starz-gold">it's you</em>.
          </>
        ) : (
          <>
            Welcome <em className="text-starz-gold">back</em>.
          </>
        )}
      </h1>
      <div className="mb-7 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      {notice && !mfaRequired && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          {notice}
        </div>
      )}

      {mfaRequired ? (
        <form onSubmit={onMfa} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray">
              Authenticator code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              className="w-full rounded-lg border border-starz-gold/25 bg-white px-4 py-2.5 text-starz-charcoal focus:outline-none focus:ring-2 focus:ring-starz-gold focus:ring-offset-2"
              required
            />
          </label>
          <FormError message={serverError} />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Verifying…" : "Verify and continue"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmit(onSignIn)} className="space-y-4">
          <Field label="Email" error={errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              {...register("email")}
              className={inputClass(!!errors.email)}
            />
          </Field>
          <Field label="Password" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="current-password"
              {...register("password")}
              className={inputClass(!!errors.password)}
            />
          </Field>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-warm-gray hover:text-starz-gold">
              Forgot password?
            </Link>
          </div>
          <FormError message={serverError} />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      )}

      {!mfaRequired && (
        <p className="mt-6 text-center text-sm text-warm-gray">
          Don't have an account?{" "}
          <Link to="/signup" className="font-medium text-starz-navy hover:text-starz-gold">
            Create one
          </Link>
        </p>
      )}
    </div>
  );
}

function inputClass(hasError: boolean) {
  return [
    "w-full rounded-lg border bg-white px-4 py-2.5 text-starz-charcoal",
    "focus:outline-none focus:ring-2 focus:ring-starz-gold focus:ring-offset-2",
    hasError ? "border-starz-brick" : "border-starz-gold/25",
  ].join(" ");
}

interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray">
        {label} {error && <span className="text-starz-gold">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-starz-brick">{error}</span>}
    </label>
  );
}
